import React from "react";
import Autocomplete from "react-google-autocomplete";


interface AddressAutocompleteProps {
  disabled?: boolean;
  placeholder?: string;
  defaultValue?: string;
  classes?: string;
  error: any;
  onAddressSelected: (
    address: string,
    lat: number,
    lng: number,
    city: string,
    state: string,
    zip: string,
    timezone: string
  ) => void;
  height?: number;
}

// Google does not always return "locality" (e.g. NYC boroughs, townships,
// unincorporated areas), so fall back through other types in priority order.
const CITY_TYPES = [
  "locality",
  "postal_town",
  "sublocality_level_1",
  "sublocality",
  "administrative_area_level_3",
  "neighborhood",
  "administrative_area_level_2",
];

const extractComponents = (components: any[] = []) => {
  const find = (type: string) =>
    components.find((c: any) => c?.types?.includes(type));

  const cityComponent = CITY_TYPES.map(find).find(Boolean);

  return {
    city: cityComponent?.long_name || "",
    state: find("administrative_area_level_1")?.short_name || "",
    zip: find("postal_code")?.long_name || "",
  };
};

// Fallback when Places details omit city/state/zip: reverse-geocode the
// coordinates and take the first result that has the missing fields.
const reverseGeocode = (lat: number, lng: number): Promise<any[]> =>
  new Promise((resolve) => {
    const google = (window as any).google;
    if (!google?.maps?.Geocoder) return resolve([]);
    new google.maps.Geocoder().geocode(
      { location: { lat, lng } },
      (results: any[], status: string) =>
        resolve(status === "OK" && results ? results : [])
    );
  });

const PlacesInput: React.FC<AddressAutocompleteProps> = ({
  disabled,
  classes,
  error,
  defaultValue,
  onAddressSelected,
  height = 52,
}) => {
  const handlePlaceSelected = async (place: any) => {
    if (place?.geometry) {
      let address = place?.formatted_address || "";
      const lat = place?.geometry?.location.lat();
      const lng = place?.geometry?.location.lng();
      let { city, state, zip } = extractComponents(place?.address_components);
      let timezone = "";

      if (!city || !state || !zip) {
        const results = await reverseGeocode(lat, lng);
        for (const result of results) {
          const fallback = extractComponents(result?.address_components);
          city = city || fallback.city;
          state = state || fallback.state;
          zip = zip || fallback.zip;
          if (city && state && zip) break;
        }
      }

      if (zip) {
        address = address.replace(zip, "").trim();
      }

      try {
        const timestamp = Math.floor(Date.now() / 1000);
        const timezoneResponse = await fetch(
          `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${process.env.REACT_APP_PLACES_API}`
        );
        const timezoneData = await timezoneResponse.json();
        timezone = timezoneData.timeZoneId;
      } catch (error) {
        console.log("Error fetching timezone:", error);
      }

      onAddressSelected(address, lat, lng, city, state, zip, timezone);
    }
  };

  return (
    <div className="w-full">
      <Autocomplete
        style={{ height: height }}
        defaultValue={defaultValue}
        apiKey={process.env.REACT_APP_PLACES_API}
        onPlaceSelected={handlePlaceSelected}
        options={{
          componentRestrictions: { country: "us" },
          types: ["address"],
        }}
        className={`w-full px-4 py-3 bg-p-gray rounded-full text-xs focus:outline-none  focus:ring-1 focus:ring-primary-red text-gray-600  ${classes} ${
          disabled ? "placeholder-black" : ""
        }`}
        disabled={disabled}
        placeholder={defaultValue ?? "Address"}
      />
      {error && <p className="text-red-600 text-sm">{error.message}</p>}
    </div>
  );
};

export default PlacesInput;
